/* Qué hizo el autor con lo que la IA le propuso, deducido del documento.
 *
 * POR QUÉ HAY QUE DEDUCIRLO. En el Studio el autor pulsa un botón y el asunto queda zanjado. En
 * Word no existe —ni va a existir— un evento de Office.js que diga «el autor acaba de aceptar
 * este cambio en la cinta Revisar». Lo que sí hay es el documento: se puede mirar si lo que
 * propusimos sigue ahí, si sigue con retoques, o si no está.
 *
 * Y ESO NO ES LO MISMO QUE SABERLO. Por eso cada veredicto viaja con su método —`reconciliacion`,
 * nunca `explicita`— y el certificado imprime cuántas decisiones son declaradas y cuántas
 * deducidas. Un dato deducido presentado como declarado sería exactamente la clase de exageración
 * que este producto existe para no cometer.
 *
 * Función pura a propósito: se prueba sin Word, sin red y sin gastar un céntimo.
 */
import { findSpan, normWithMap } from "./match";

export type Decision = "aceptado" | "editado" | "descartado" | "pendiente";

export interface Veredicto {
  decision: Decision;
  /** 1 cuando el texto está intacto; entre 0 y 1 cuando se retocó; 0 si no aparece. */
  similitud: number;
}

/** Palabras normalizadas, para comparar dos textos sin que las comillas de Word estorben. */
function palabras(s: string): string[] {
  return normWithMap(s).norm.split(" ").filter(Boolean);
}

/**
 * Parecido entre dos textos, por coincidencia de palabras (Dice).
 *
 * Se elige esta y no una distancia de edición porque lo que interesa es «¿queda mi propuesta
 * aquí debajo?», no cuántas pulsaciones hay entre una y otra. Cambiar dos palabras de veinte
 * tiene que seguir siendo «lo retocó», y una frase distinta que casualmente empiece igual, no.
 */
export function parecido(a: string, b: string): number {
  const pa = palabras(a);
  const pb = palabras(b);
  if (!pa.length || !pb.length) return 0;
  const bolsa = new Map<string, number>();
  for (const w of pb) bolsa.set(w, (bolsa.get(w) ?? 0) + 1);
  let comunes = 0;
  for (const w of pa) {
    const n = bolsa.get(w) ?? 0;
    if (n > 0) {
      comunes++;
      bolsa.set(w, n - 1);
    }
  }
  return (2 * comunes) / (pa.length + pb.length);
}

/**
 * El veredicto.
 *
 * @param propuesta        lo que la IA devolvió
 * @param cuerpo           el texto del documento tal y como está ahora
 * @param cambiosPendientes el texto de los cambios de control aún sin aceptar ni rechazar
 * @param umbral           por debajo de este parecido ya no es «retocado», es otra cosa
 *
 * El orden importa: primero se mira si el cambio sigue EN EL AIRE. Un cambio pendiente no es una
 * decisión —el autor todavía no ha dicho nada— y contarlo como aceptado sería ponerle palabras
 * en la boca.
 */
export function clasificar(
  propuesta: string,
  cuerpo: string,
  cambiosPendientes: string[] = [],
  umbral = 0.6,
): Veredicto {
  const limpia = String(propuesta ?? "").trim();
  if (!limpia) return { decision: "descartado", similitud: 0 };

  // 1 · ¿Sigue siendo un cambio sin revisar? Entonces no hay decisión que registrar.
  for (const c of cambiosPendientes) {
    if (parecido(limpia, c) >= 0.9 || (c && normWithMap(c).norm.includes(normWithMap(limpia).norm))) {
      return { decision: "pendiente", similitud: 1 };
    }
  }

  const cuerpoN = String(cuerpo ?? "");
  if (!cuerpoN.trim()) return { decision: "descartado", similitud: 0 };

  // 2 · Intacto en el documento: el autor se lo quedó tal cual.
  const exacto = findSpan(cuerpoN, limpia);
  if (exacto && !exacto.approx) return { decision: "aceptado", similitud: 1 };

  // 3 · Casó solo el principio: está, pero con la mano del autor encima. Se mide cuánto queda.
  if (exacto) {
    const tramo = cuerpoN.slice(exacto.start, exacto.end);
    const s = parecido(limpia, tramo);
    return s >= umbral ? { decision: "editado", similitud: redondea(s) } : { decision: "descartado", similitud: redondea(s) };
  }

  // 4 · Ni el principio casa. Puede que el autor cambiara justo la primera frase, así que se
  // prueba con un anclaje más corto antes de dar la propuesta por muerta.
  const corto = findSpan(cuerpoN, limpia, 20);
  if (corto) {
    const tramo = cuerpoN.slice(corto.start, corto.end);
    const s = parecido(limpia, tramo);
    if (s >= umbral) return { decision: "editado", similitud: redondea(s) };
  }

  return { decision: "descartado", similitud: 0 };
}

const redondea = (n: number) => Math.round(n * 100) / 100;
